





















import { defineComponent, ref, computed, onBeforeUnmount, set } from "@vue/composition-api";
import { throttle } from "lodash-es";
import { BlockingStatus } from "@scrile/api-provider/dist/api/BlockingStatusProvider";
import { InputMessageThreadSearch, MessageThread } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { Subscription } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import { useAnnouncements } from "@/hooks/useAnnouncements";
import useAuthUser from "@/hooks/useAuthUser";
import { useGroups } from "@/hooks/useGroups";
import usePager from "@/hooks/usePager";
import useSessionStorage, { USER_SETTINGS } from "@/hooks/useSessionStorage";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { getThreadOpponentId } from "@/lib/opponentIdHelper";
import { EVENTS } from "@/const";
import MessageThreadsView from "./components/MessageThreadsView.vue";
import { MessageSortTypeEnum } from "./types";

export default defineComponent({
  name: "PageMessageThreads",
  components: { MessageThreadsView },
  setup(_, { root }) {
    const { getUserSetting, setUserSetting } = useSessionStorage();
    const { authorities: authUserAuthorities, authUserStrict, isCreator } = useAuthUser();
    const { groups, getData: getGroupsData } = useGroups({ system: true });
    const { hasUnreadAnnouncement, announcements, getAnnouncements } = useAnnouncements();

    const loading = ref(false);
    const blockingStatus = ref<BlockingStatus[]>([]);
    const subscribers = ref<Record<string, Subscription>>({});
    const userMessageSetting = ref<Record<string, any>>({});
    const canManageThreads = ref(false);

    const allowMassMessage = computed(() => groups.value.some(i => i.memberCount > 1));
    const allowMassMessageStat = computed(() =>
      checkAuthorities(authUserAuthorities.value, ["updateMyContentPrice", "getMyMassMessageStat"])
    );

    const currentSortItem = computed(() => userMessageSetting.value?.sort || MessageSortTypeEnum.DEFAULT);

    const sortItemsArray = [
      {
        sort: "CURRENT_USER_PIN_TIME;UPDATED_TIME,DESC",
      },
      {
        sort: "CURRENT_USER_PIN_TIME;UPDATED_TIME,DESC",
        unreadOnly: true,
      },
      {
        sort: "CURRENT_USER_PIN_TIME;UPDATED_TIME,ASC",
        unreadOnly: true,
      },
    ];

    const getGroups = async () => {
      if (checkAuthorities(authUserAuthorities.value, ["updateMyContentPrice"])) {
        await getGroupsData();
      }
    };

    const getUserMessageSetting = () => {
      userMessageSetting.value = getUserSetting(USER_SETTINGS.MESSAGE_SETTINGS);
    };

    const saveUserMessageSetting = (newSettings: Record<string, any>) => {
      userMessageSetting.value = { ...userMessageSetting.value, ...newSettings };
      setUserSetting(USER_SETTINGS.MESSAGE_SETTINGS, userMessageSetting.value);
    };

    const getSubscribers = async (threads: MessageThread[]) => {
      if (!threads.length || !isCreator.value) return;
      const consumerIds: string[] = [];
      const userIdToThreadId: Record<string, string> = {};
      threads.map(i => {
        const id = getThreadOpponentId(i.userIds, authUserStrict.value.id);
        if (!!id) {
          consumerIds.push(id);
          userIdToThreadId[id] = i.id;
        }
      });
      const response = await providers.SubscriptionProvider.search({
        data: {
          consumerIds,
          providerId: authUserStrict.value.id,
          size: consumerIds.length,
          expired: false,
        },
      });
      response.result.map(i => {
        if (userIdToThreadId[i.consumerId]) set(subscribers.value, userIdToThreadId[i.consumerId], i);
      });
    };

    const getBlockingStatusList = async (messageThreads: MessageThread[]) => {
      if (!messageThreads.length) return;
      const opponentIds: string[] = [];
      messageThreads.forEach(thread => {
        const id = getThreadOpponentId(thread.userIds, authUserStrict.value.id);
        id && opponentIds.push(id);
      });
      const result = await providers.BlockingStatusProvider.searchByOpponents({ data: { opponentIds } });
      blockingStatus.value = blockingStatus.value.concat(result);
    };

    const {
      data: messageThreadsSearchResult,
      items: messageThreadList,
      enableScroll,
      getFirstPage: getThreads,
      getNextPage,
      removeItem,
    } = usePager<MessageThread, InputMessageThreadSearch>(
      (page, requestData: InputMessageThreadSearch = { includeDeleted: false, includeHidden: false }) => {
        const sortData = { ...sortItemsArray[currentSortItem.value] };
        return providers.MessageThreadsProvider.find({
          data: {
            userId: authUserStrict.value.id,
            page,
            size: 20,
            ...requestData,
            ...sortData,
          },
        });
      },
      response => {
        getSubscribers(response);
        getBlockingStatusList(response);
      }
    );

    const getPrivateMessageSettings = async () => {
      if (checkAuthorities(authUserAuthorities.value, ["getMyMessagePrivateUserSettings"])) {
        const response = await providers.MessageSettingsProvider.searchPrivate({ userId: authUserStrict.value.id });
        canManageThreads.value = response.allowManagement;
      }
    };

    const removeThreadItem = async (thread: MessageThread) => {
      await removeItem(item => item.id === thread.id);
      emitter.emit(EVENTS.MESSAGES_UPDATE_MESSAGE_UNREAD_COUNT);
    };

    const onHideThread = async (thread: MessageThread) => {
      const response = await providers.MessageThreadsProvider.hide({ id: thread.id });
      if (response) {
        await removeThreadItem(thread);
      }
    };

    const onDeleteThread = async (thread: MessageThread) => {
      const response = await providers.MessageThreadsProvider.deleteForMe({ data: { threadId: thread.id } });
      if (response) {
        await removeThreadItem(thread);
      }
    };

    const onSort = async (select: MessageSortTypeEnum) => {
      saveUserMessageSetting({ sort: select });
      await getThreads();
    };

    const onMassMessage = () => {
      root.$router.push({ name: "massMessage" });
    };

    const onMessage = throttle(() => getThreads(), 3000, { trailing: false });

    const onAnnouncement = async () => {
      await getAnnouncements({ size: 1 });
    };

    const getPageData = async () => {
      try {
        loading.value = true;
        getUserMessageSetting();
        await Promise.all([getThreads(), getGroups(), getPrivateMessageSettings(), getAnnouncements({ size: 1 })]);
      } finally {
        loading.value = false;
      }
    };

    getPageData();
    emitter.on<void>(EVENTS.MESSAGES_NEW_MESSAGE, onMessage);
    emitter.on<void>(EVENTS.MESSAGES_MARKED_READ, onMessage);
    emitter.on<void>(EVENTS.ANNOUNCEMENT_NEW_ANNOUNCEMENT, onAnnouncement);

    onBeforeUnmount(() => {
      emitter.off<void>(EVENTS.MESSAGES_NEW_MESSAGE, onMessage);
      emitter.off<void>(EVENTS.MESSAGES_MARKED_READ, onMessage);
      emitter.off<void>(EVENTS.ANNOUNCEMENT_NEW_ANNOUNCEMENT, onAnnouncement);
    });

    return {
      loading,
      authUserStrict,
      enableScroll,
      messageThreadList,
      subscribers,
      allowMassMessage,
      allowMassMessageStat,
      currentSortItem,
      hasUnreadAnnouncement,
      announcements,
      blockingStatus,
      canManageThreads,
      getNextPage,
      messageThreadsSearchResult,
      onHideThread,
      onSort,
      onMassMessage,
      onDeleteThread,
    };
  },
});
